import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Link from "@cloudscape-design/components/link";
import type { ReactNode } from "react";
import { navigate, useLocation } from "../../router/useRoute";
import { PublicShell } from "./PublicShell";

/**
 * Email và số điện thoại phải là link, không phải chữ thường.
 *
 * Trên điện thoại — nơi phần lớn người dùng đọc mấy trang này — chữ thường bắt
 * người ta bôi đen, copy, rồi tự mở ứng dụng mail hoặc bàn phím số. `mailto:`
 * và `tel:` bỏ hẳn ba bước đó.
 */
const CONTACT_EMAIL = "bmt.m.tien@gmail.com";
const CONTACT_PHONE = "086 858 8537";
const MailLink = () => <Link href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link>;
const PhoneLink = () => <Link href={`tel:+84868588537`}>{CONTACT_PHONE}</Link>;

interface Section {
  heading: string;
  body: ReactNode;
  /**
   * Danh sách gạch đầu dòng dưới phần thân.
   *
   * Chính sách bảo mật phải liệt kê từng nhóm dữ liệu, từng quyền nhạy cảm và
   * từng bên thứ ba nhận dữ liệu — Chrome Web Store đối chiếu trang này với
   * phần khai báo trong bảng điều khiển. Nhồi mười hai cái tên vào một đoạn văn
   * thì đúng về nội dung nhưng không ai đối chiếu nổi.
   */
  items?: ReactNode[];
}

interface Document {
  title: string;
  description: string;
  sections: Section[];
  /** Ngày sửa nội dung gần nhất. Sửa chữ trong trang thì phải sửa cả ngày này. */
  updated?: string;
}

const DOCUMENTS: Record<string, Document> = {
  "/terms": {
    title: "Điều khoản sử dụng",
    description: "Phạm vi quyền sử dụng tài khoản FaceGo và trách nhiệm của người dùng.",
    sections: [
      {
        heading: "Quyền sử dụng",
        body: "Tài khoản FaceGo được cấp cho người đăng ký và tuân theo số lượng, tính năng và thời hạn của gói hiện hành.",
      },
      {
        heading: "Quản lý Fanpage",
        body: "Công cụ FaceGo làm việc trên các Fanpage, nhóm và trang cá nhân của tài khoản Facebook đang đăng nhập trong trình duyệt. Bạn chọn Fanpage cho từng lượt chạy ngay trong extension; máy chủ xác nhận quyền và thời hạn gói trước khi áp dụng.",
      },
      {
        heading: "Trách nhiệm",
        body: "Bạn chịu trách nhiệm với nội dung và hoạt động tự động hóa được thực hiện qua tài khoản Facebook của mình.",
      },
    ],
  },
  /*
    Nội dung trang này phải phủ đúng những gì khai trong
    `docs/chrome-web-store/PRIVACY_PRACTICES.md` của extension. Hai bên lệch nhau
    là một trong những lý do bị từ chối phổ biến nhất, và FaceGo lại xin đúng
    nhóm quyền nhạy cảm — `cookies`, `webRequest`, host Facebook. Sửa một bên thì
    phải sửa bên kia.
  */
  "/privacy": {
    title: "Chính sách bảo mật",
    description: "Dữ liệu FaceGo thu thập, dùng vào việc gì, lưu bao lâu, chia sẻ với ai và cách bạn kiểm soát chúng.",
    updated: "Cập nhật lần cuối: 19/10/2026",
    sections: [
      {
        heading: "Phạm vi và mục đích",
        body: "FaceGo là công cụ dành cho người dùng đã đăng nhập, để quản lý, đăng, hẹn giờ, phân tích và tự động hoá nội dung cùng tương tác cho Fanpage Facebook và các kênh đăng liên quan. Mọi dữ liệu nêu dưới đây chỉ được thu thập khi cần cho đúng mục đích đó, và chỉ khi bạn chạy công cụ tương ứng.",
      },
      {
        heading: "Dữ liệu tài khoản",
        body: "FaceGo lưu email, tên hiển thị, số điện thoại, mật khẩu đã băm, thông tin gói cước và các định danh Facebook (ID người dùng, ID Fanpage) cần để vận hành các công cụ trên Fanpage của bạn.",
      },
      {
        heading: "Đăng nhập bằng Google",
        body: "Khi bạn chọn \"Tiếp tục với Google\", FaceGo chỉ xin các quyền openid, email và profile, và chỉ nhận từ Google: địa chỉ email đã được Google xác minh, tên hiển thị và ảnh đại diện. Dữ liệu này dùng để tạo hoặc đăng nhập vào tài khoản FaceGo của bạn — nếu email trùng một tài khoản FaceGo đã có thì vào đúng tài khoản đó. FaceGo không đọc Gmail, danh bạ, Drive hay bất kỳ dữ liệu Google nào khác, không lưu mật khẩu Google và không chia sẻ dữ liệu này cho bên thứ ba. Việc FaceGo sử dụng thông tin nhận từ API của Google tuân thủ Chính sách dữ liệu người dùng của Dịch vụ API Google, bao gồm các yêu cầu về Sử dụng giới hạn. Bạn có thể gỡ quyền của FaceGo bất cứ lúc nào tại myaccount.google.com/permissions.",
      },
      {
        heading: "Phiên đăng nhập và khoá truy cập",
        body: "Token phiên FaceGo chỉ được lưu ở máy chủ dưới dạng băm SHA-256 và bị thu hồi khi bạn đăng xuất hoặc đổi mật khẩu. Page access token của Facebook và thông tin kết nối của các tích hợp bạn tự cấu hình được mã hoá khi lưu trữ.",
      },
      {
        heading: "Quyền nhạy cảm của extension",
        body: "Extension xin những quyền sau, mỗi quyền phục vụ đúng một việc:",
        items: [
          "cookies — đọc phiên Facebook và phiên tích hợp đang hoạt động, để thao tác đúng tài khoản bạn đang mở.",
          "webRequest — chỉ để nhận diện access token Pancake trong request gửi tới Pancake, và chỉ khi bạn bật tích hợp đó.",
          "identity — mở cửa sổ đăng nhập của Google khi bạn chọn \"Tiếp tục với Google\"; không dùng vào việc gì khác.",
          "Quyền truy cập trang Facebook — hiển thị giao diện FaceGo và thực hiện thao tác quản lý Fanpage trong phiên bạn đang đăng nhập.",
          "Quyền truy cập TikTok, YouTube, Shopee, Pancake — chỉ thu thập nội dung hoặc dữ liệu kết nối cho đúng công cụ bạn chọn chạy.",
        ],
      },
      {
        heading: "Nội dung và dữ liệu công việc",
        body: "Tuỳ công cụ bạn chọn, FaceGo đọc và ghi dữ liệu Fanpage, bài viết, bình luận, media, tệp tương tác, dữ liệu quảng cáo và chiến dịch. Đây là dữ liệu của chính các tài khoản và trang bạn có quyền quản lý.",
      },
      {
        heading: "Nhật ký hoạt động",
        body: "Lịch sử đăng nhập, sự kiện extension, nhật ký tự động hoá, kết quả đăng bài và sự kiện lỗi được dùng để bảo vệ tài khoản, hỗ trợ kỹ thuật và hiển thị bảng điều khiển của bạn.",
      },
      {
        heading: "Dữ liệu thanh toán",
        body: "FaceGo lưu thông tin giao dịch ngân hàng ở mức đủ để đối soát gói cước đã thanh toán. FaceGo không thu thập số thẻ đầy đủ và không lưu dữ liệu thẻ.",
      },
      {
        heading: "Thống kê truy cập facego.site",
        body: "Website facego.site dùng Google Analytics 4 để đếm lượt xem trang, phiên truy cập và nguồn truy cập (thông số utm của chiến dịch), nhằm biết trang nào hữu ích và cải thiện website. Google Analytics đặt cookie _ga trên trình duyệt của bạn. FaceGo không gửi sang Google token, mã xác minh, mã giới thiệu hay bất kỳ tham số nào khác trong đường dẫn, không gửi email hay dữ liệu tài khoản, và đã tắt Google Signals cùng việc dùng dữ liệu để cá nhân hoá quảng cáo. Extension FaceGo không dùng Google Analytics. Bạn có thể chặn việc thống kê bằng tiện ích Google Analytics Opt-out (tools.google.com/dlpage/gaoptout) hoặc chặn cookie của facego.site trong trình duyệt.",
      },
      {
        heading: "Vị trí",
        body: "FaceGo không thu thập vị trí chính xác. Múi giờ hoặc nhãn khu vực có thể được suy ra từ chính dữ liệu Facebook mà bạn chọn xử lý.",
      },
      {
        heading: "Thời gian lưu trữ",
        body: "Dữ liệu không được giữ lâu hơn mức cần thiết:",
        items: [
          "Nhật ký sản phẩm, kết quả quét và dữ liệu tệp tương tác: tối đa 180 ngày.",
          "Media tạm, bộ nhớ đệm và bản sao lưu: hết hạn sau 30 ngày.",
          "Dữ liệu tài khoản và gói cước: giữ tới khi bạn yêu cầu xoá tài khoản.",
        ],
      },
      {
        heading: "Chia sẻ với bên thứ ba",
        body: "Tuỳ công cụ bạn chọn chạy, dữ liệu có thể được gửi tới các dịch vụ sau để thực hiện đúng thao tác bạn yêu cầu:",
        items: [
          "Meta/Facebook — đăng bài, đọc dữ liệu Fanpage và quảng cáo.",
          "Google — xác minh danh tính khi bạn chọn đăng nhập bằng Google; Google Analytics thống kê lượt truy cập facego.site.",
          "Buffer, Pancake — các tích hợp đăng bài và chăm sóc khách hàng do bạn tự kết nối.",
          "ImgBB, Unsplash, Pexels — lưu trữ và lấy hình ảnh.",
          "OpenRouter — sinh nội dung bằng AI khi bạn dùng tính năng đó.",
          "TikTok, YouTube — lấy video nguồn cho công cụ reup.",
          "SePay và ngân hàng nhận tiền — xử lý và đối soát thanh toán.",
          "Cloudflare R2 và các nhà cung cấp hạ tầng của FaceGo — lưu trữ và vận hành hệ thống.",
        ],
      },
      {
        heading: "Những điều FaceGo không làm",
        body: "Có những việc FaceGo cam kết không thực hiện với dữ liệu của bạn:",
        items: [
          "Không bán hoặc chuyển dữ liệu cho môi giới dữ liệu hay mạng quảng cáo.",
          "Không dùng dữ liệu cho mục đích không liên quan tới các chức năng đã công bố.",
          "Không dùng dữ liệu để xác định khả năng thanh toán hoặc phục vụ hoạt động cho vay.",
          "Không dùng thông tin đăng nhập của bạn cho quảng cáo.",
        ],
      },
      {
        heading: "Bảo mật",
        body: "Toàn bộ dữ liệu được truyền qua HTTPS. Thông tin kết nối nhạy cảm được mã hoá khi lưu trữ, còn token phiên chỉ tồn tại ở dạng băm SHA-256 nên máy chủ không đọc lại được giá trị gốc.",
      },
      {
        heading: "Quyền của bạn",
        body: "Bạn kiểm soát dữ liệu của mình bằng các cách sau:",
        items: [
          "Xem và sửa thông tin tài khoản trong trang thành viên FaceGo.",
          "Ngừng cho FaceGo truy cập một Fanpage bằng cách thu hồi quyền quản trị Fanpage đó khỏi tài khoản Facebook đang đăng nhập, hoặc đăng xuất tài khoản Facebook khỏi trình duyệt.",
          <>Yêu cầu xoá toàn bộ tài khoản và dữ liệu tại <Link href="/account/delete">facego.site/account/delete</Link>.</>,
          "Thu hồi quyền của extension bất cứ lúc nào bằng cách gỡ tiện ích khỏi trình duyệt.",
        ],
      },
      {
        heading: "Liên hệ",
        body: <>Câu hỏi về quyền riêng tư xin gửi tới <MailLink />, hoặc Zalo/hotline <PhoneLink />.</>,
      },
      {
        heading: "Thay đổi chính sách",
        body: "Khi hành vi của sản phẩm thay đổi, trang này được cập nhật trước và ngày ở đầu trang cho biết lần sửa gần nhất. Thay đổi làm mở rộng phạm vi dữ liệu thu thập sẽ được thông báo trong ứng dụng.",
      },
    ],
  },
  /*
    Đường dẫn này đã khai với Chrome Web Store là nơi yêu cầu xoá dữ liệu, nên
    nó phải đọc được khi **chưa đăng nhập** — người duyệt không có tài khoản.
  */
  "/account/delete": {
    title: "Yêu cầu xoá tài khoản",
    description: "Cách yêu cầu xoá tài khoản FaceGo cùng toàn bộ dữ liệu liên quan.",
    updated: "Cập nhật lần cuối: 29/09/2026",
    sections: [
      {
        heading: "Cách gửi yêu cầu",
        body: <>Gửi email tới <MailLink /> từ chính địa chỉ email đã đăng ký FaceGo, tiêu đề “Yêu cầu xoá tài khoản”. Nếu tiện hơn, bạn có thể nhắn Zalo/hotline <PhoneLink /> — nhưng vẫn cần xác minh quyền sở hữu email trước khi xoá.</>,
      },
      {
        heading: "Những gì sẽ bị xoá",
        body: "Yêu cầu được xử lý sẽ xoá:",
        items: [
          "Thông tin tài khoản: email, tên hiển thị, số điện thoại, mật khẩu đã băm.",
          "Danh sách Fanpage đã đồng bộ, Page access token và thông tin kết nối của các tích hợp bạn đã cấu hình.",
          "Nhật ký hoạt động, kết quả quét, dữ liệu tệp tương tác và media tạm gắn với tài khoản.",
        ],
      },
      {
        heading: "Những gì được giữ lại",
        body: "Hồ sơ giao dịch thanh toán được giữ theo yêu cầu kế toán và pháp lý, ở mức tối thiểu để đối soát, và không còn liên kết với hoạt động sử dụng sản phẩm.",
      },
      {
        heading: "Thời gian xử lý",
        body: "Yêu cầu được xác nhận trong vòng 7 ngày làm việc và hoàn tất xoá trong vòng 30 ngày. Bản sao lưu chứa dữ liệu cũ sẽ hết hạn trong tối đa 30 ngày sau đó.",
      },
      {
        heading: "Trước khi xoá",
        body: "Xoá tài khoản là thao tác không khôi phục được: gói cước còn hạn và toàn bộ lịch đăng đang chờ đều mất theo. Nếu bạn chỉ muốn dừng truy cập, gỡ extension khỏi trình duyệt là đủ.",
      },
    ],
  },
  "/support": {
    title: "Trung tâm hỗ trợ",
    description: "Các bước tự kiểm tra trước khi liên hệ đội hỗ trợ FaceGo.",
    sections: [
      {
        heading: "Sự cố đăng nhập",
        body: "Kiểm tra email xác minh, thử đặt lại mật khẩu và bảo đảm extension đang trỏ đúng API FaceGo.",
      },
      {
        heading: "Bảo vệ tài khoản",
        body: "Không gửi mật khẩu, cookie Facebook hoặc token đăng nhập cho bất kỳ ai, kể cả người tự xưng là hỗ trợ FaceGo.",
      },
      {
        heading: "Liên hệ",
        body: <>Zalo hoặc hotline <PhoneLink />, email <MailLink />. Gửi kèm ảnh chụp lỗi để được kiểm tra nhanh hơn.</>,
      },
    ],
  },
};

export function LegalPage() {
  const { pathname } = useLocation();
  const document = DOCUMENTS[pathname] ?? DOCUMENTS["/terms"]!;

  return (
    <PublicShell title={document.title} description={document.description} hero="compact">
      <SpaceBetween size="l">
        {document.updated ? (
          <Box variant="small" color="text-body-secondary">
            {document.updated}
          </Box>
        ) : null}
        {document.sections.map((section) => (
          <div key={section.heading}>
            <Box variant="h3">{section.heading}</Box>
            <Box variant="p" color="text-body-secondary">
              {section.body}
            </Box>
            {section.items ? (
              <Box color="text-body-secondary">
                <ul>
                  {/* Khoá theo chỉ số: danh sách là hằng số trong file này,
                      không sắp xếp lại và không thêm bớt lúc chạy. */}
                  {section.items.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </Box>
            ) : null}
          </div>
        ))}
        <Box>
          <Button
            href="/login"
            onFollow={(event) => {
              event.preventDefault();
              navigate("/login");
            }}
          >
            Quay lại đăng nhập
          </Button>
        </Box>
      </SpaceBetween>
    </PublicShell>
  );
}
